import DataTable, { useDataTableHandle } from '../DataTable';
import { ExternalLinkAlt } from '@styled-icons/fa-solid';
import { getNeeNorDetails, postNeeNorLazyLoad } from '../apiCalls';
import { Container } from 'react-bootstrap';
import isoDateConverter from '../isoDateConverter';
import { useSecurity } from '../Security/security';
import { useState, useEffect, useContext } from 'react';
import { StoreContext } from '../Store';
import NeeNorFilters from './NeeNorFilters';
import ResetButton from '../Buttons/ResetButton';

const DEFAULT_FILTER_VALUES = {
  companyName: null,
  shortCode: null,
  eori: null,
  lastModifiedFrom: null,
  lastModifiedTo: null,
};

function NeeNorList({ neeNors, setNeeNor, setEditCreate, setNeeNors, origin = 'neenor' }) {
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState(DEFAULT_FILTER_VALUES);
  const [isLoading, setIsLoading] = useState(false);
  const store = useContext(StoreContext);
  const dth = useDataTableHandle();
  const persistedFilters = store.neeNorFilters[0]?.filters || {};
  const hasFiltersApplied = !!Object.keys(persistedFilters).length || dth?.current?.sortStatus?.currentKey;

  const buildFilterPayload = (filters) => {
    const f = filters || persistedFilters;
    return {
      companyName: f.companyName || null,
      shortCode: f.shortCode || null,
      eori: f.eori || null,
      lastModifiedFrom: f.lastModifiedFrom || null,
      lastModifiedTo: f.lastModifiedTo || null,
    };
  };

  async function fetchData(filters, goToFirstPage = false, resetSort = false) {
    if (origin == 'bp') return;
    try {
      const pagination = dth.current.pagination;
      const sortStatus = resetSort ? {} : dth.current.sortStatus;
      const payload = {
        skip: goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus.currentKey,
        sort_order: sortStatus.direction,
        filter: buildFilterPayload(filters),
      };
      setIsLoading(true);
      const data = await postNeeNorLazyLoad(payload);
      dth.current.setServerDataLength(data.totalItems);
      setNeeNors(data.data);
      if (goToFirstPage) dth.current.resetPagination();
    } catch (err) {
      console.error('Error retrieving data');
    } finally {
      setIsLoading(false);
    }
  }

  async function updateData(setPaginationLength, pagination, sortStatus, search, filter) {
    if (origin == 'bp') return;
    try {
      const filtersToSend = buildFilterPayload(persistedFilters);
      const data = await postNeeNorLazyLoad({
        skip: pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus.currentKey,
        sort_order: sortStatus.direction,
        search,
        filter: { ...filtersToSend, ...filter },
      });
      setPaginationLength(data.totalItems);
      setNeeNors(data.data);
    } catch (err) {
      console.error('Error retrieving data');
    }
  }

  useEffect(() => {
    if (hasFiltersApplied) {
      setFilterData({ ...DEFAULT_FILTER_VALUES, ...persistedFilters });
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (paginationLoaded) fetchData();
  }, [paginationLoaded]);

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus({});
    store.neeNorFilters[1]({});
    setFilterData(DEFAULT_FILTER_VALUES);
    await fetchData({}, true, true);
  };

  const handleSubmit = async () => {
    store.neeNorFilters[1]({ filters: { ...filterData } });
    await fetchData({ ...filterData }, true);
  };

  const { securityState, validateRole } = useSecurity();

  const getNeenor = async (id) => {
    try {
      const data = await getNeeNorDetails(id);
      setNeeNor(data[0]);
    } catch (err) {
      console.error(err);
    }
  };

  const showFilterPanel = origin === 'neenor';

  return (
    <Container className="container-md">
      <section className="floating-box" style={{ maxWidth: '1600px' }}>
        <div className="d-flex justify-content-between align-items-center px-3 pt-4">
          <h1 className="page-heading">{origin == 'bp' ? 'Business Partner' : 'NeeNor'}</h1>
          {showFilterPanel && (
            <div className="d-flex gap-4 align-items-center flex-shrink-0">
              {hasFiltersApplied && <ResetButton onClick={handleReset} />}
              <button
                className="stepper-button"
                onClick={() => setShowFilters(!showFilters)}
                style={{ minWidth: 145, maxWidth: 145 }}
                disabled={hasFiltersApplied && showFilters}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
            </div>
          )}
        </div>

        {showFilterPanel && (
          <NeeNorFilters visible={showFilters} values={filterData} onChange={setFilterData} onSubmit={handleSubmit} />
        )}

        <div className="px-3 pt-4">
          <DataTable
            handle={dth}
            lazyLoading
            filterable={false}
            sortable
            data={neeNors}
            className="table-striped"
            advancedSearch
            showAdvnSearch={
              !validateRole({ department: 'TRADER' }) && !validateRole({ department: 'LSP' }) && !showFilterPanel
            }
            onUpdate={updateData}
            onPaginationLoaded={() => {
              setPaginationLoaded(true);
            }}
            persistGetter={store.neeNorPersist[0]}
            persistSetter={store.neeNorPersist[1]}
            isLoading={isLoading}
          >
            <DataTable.Head>
              <section className="d-flex justify-content-end datatable-button">
                <button
                  type="button"
                  className="stepper-button yellow"
                  onClick={() => {
                    setEditCreate('create');
                    setNeeNor({});
                  }}
                >
                  New
                </button>
              </section>
            </DataTable.Head>
            <DataTable.Pagination itemsPerPage={[10, 15, 50]} areaPadding={'0px'} />
            <DataTable.Headers
              style={{
                fontSize: '14.4px',
                backgroundColor: '#fff',
                paddingBottom: '10px',
                borderBottom: '1px solid #ddd',
                borderTop: '1px solid #ddd',
              }}
            />
            <DataTable.Column
              colKey="companyName"
              header="Company Name"
              searchOpts={{ type: 'text', sendAs: 'companyName' }}
            >
              {(item) => {
                return item.companyName?.slice(0, 30) + (item.companyName?.length > 30 ? '...' : '');
              }}
            </DataTable.Column>
            <DataTable.Column colKey="shortCode" header="Short Code" searchOpts={{ type: 'text', sendAs: 'shortCode' }}>
              {(item) => {
                return item.shortCode?.slice(0, 10) + (item.shortCode?.length > 10 ? '...' : '');
              }}
            </DataTable.Column>
            <DataTable.Column colKey="eori" header="EORI Number" searchOpts={{ type: 'text', sendAs: 'eori' }} />
            <DataTable.Column colKey="lastmodified" header="Last Updated" hideFilter>
              {(item) => {
                return isoDateConverter(item.lastmodified);
              }}
            </DataTable.Column>

            <DataTable.Column header="View" style={{ height: '1px', padding: 0 }} headerStyle={{ textAlign: 'center' }}>
              {(query) => (
                <button
                  className="external-table-link"
                  onClick={() => {
                    setEditCreate('edit');
                    getNeenor(query.id);
                  }}
                >
                  <ExternalLinkAlt width="1.1rem" height="1.1rem" color="#00857d" />
                </button>
              )}
            </DataTable.Column>
          </DataTable>
        </div>
      </section>
    </Container>
  );
}

export default NeeNorList;
